import { instance } from "api/apiBase";
import { PropertyKind } from "shared/types";
import { Address, ApartmentsComVariant } from "types/landlord/address";

export type SyndicationFeed =
  | "zillow"
  | "zumper"
  | "apartments_com"
  | "dwellsy"
  | "affordable_housing_com";

interface FeedMoney {
  cents: number;
  currency: string;
}

export interface FeedListing {
  id: number;
  title: string | null;
  active: boolean;
  monthly_rent_amount: FeedMoney | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  feed_zillow_enabled: boolean;
  feed_zillow_spotlight_enabled: boolean;
  feed_zumper_enabled: boolean;
  feed_apartments_com_enabled: boolean;
  feed_apartments_com_variant: ApartmentsComVariant | null;
  feed_dwellsy_enabled: boolean;
  feed_affordable_housing_com_enabled: boolean;
  feed_zillow_enabled_at: string | null;
  feed_zumper_enabled_at: string | null;
  feed_apartments_com_enabled_at: string | null;
  feed_dwellsy_enabled_at: string | null;
  feed_affordable_housing_com_enabled_at: string | null;
  unit: { id: number; title: string | null; listed: boolean } | null;
  property: {
    id: number;
    name: string;
    property_group: { id: number; name: string; index: number } | null;
    address: Address | null;
    owner: string | null;
    kind: PropertyKind | null;
  };
}

interface FeedListingsPage {
  items: FeedListing[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
}

interface ToggleFeedListingPayload {
  enabled?: boolean;
  variant?: ApartmentsComVariant;
  spotlight_enabled?: boolean;
}

// The timestamp column name differs per feed; map it once so callers can read
// the right "listed on" field without string-templating into the type.
export const FEED_ENABLED_AT_FIELD: Record<
  SyndicationFeed,
  | "feed_zillow_enabled_at"
  | "feed_zumper_enabled_at"
  | "feed_apartments_com_enabled_at"
  | "feed_dwellsy_enabled_at"
  | "feed_affordable_housing_com_enabled_at"
> = {
  zillow: "feed_zillow_enabled_at",
  zumper: "feed_zumper_enabled_at",
  apartments_com: "feed_apartments_com_enabled_at",
  dwellsy: "feed_dwellsy_enabled_at",
  affordable_housing_com: "feed_affordable_housing_com_enabled_at"
};

const feedListingsUrl = (feed: SyndicationFeed) =>
  `/portal/v1/external_integrations/${feed}/listings`;

export const syndicationFeedApi = {
  getFeedListings: async (
    feed: SyndicationFeed,
    { page = 1, per_page = 50 }: { page?: number; per_page?: number } = {}
  ): Promise<FeedListingsPage> =>
    (
      await instance.get<FeedListingsPage>(feedListingsUrl(feed), {
        params: { page, per_page }
      })
    ).data,

  toggleFeedListing: async (
    feed: SyndicationFeed,
    unitId: number,
    payload: ToggleFeedListingPayload
  ): Promise<FeedListing> =>
    (
      await instance.patch<FeedListing>(
        `${feedListingsUrl(feed)}/${unitId}`,
        payload
      )
    ).data
};
